import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Image, Trash2, RefreshCw, Film, FileImage, Crop, Download, Archive, Wand2, Play, StickyNote, Check, RotateCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import FocalPointEditor from "@/components/FocalPointEditor";
import StorageOverviewCard from "@/components/StorageOverviewCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaPage() {
  const [companies, setCompanies] = useState([]);
  const [uploadNote, setUploadNote] = useState("");
  const [noteEdit, setNoteEdit] = useState(null); // {id, name, text}
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [thumbnails, setThumbnails] = useState({});
  const [variantThumbs, setVariantThumbs] = useState({}); // { mediaId: { instagram_post: blobUrl, instagram_story: blobUrl } }
  const [variantDialog, setVariantDialog] = useState(null); // selected media item
  const [previewVideo, setPreviewVideo] = useState(null); // {url, name}
  const [backfillPreview, setBackfillPreview] = useState(null); // { total_candidates, sample, loading }
  const [backfilling, setBackfilling] = useState(false);
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // [{key,name,status,detail,file,companyId,note}]
  const processingRef = useRef(false);
  const queueRef = useRef([]);
  const activeUploads = uploadQueue.filter((it) => it.status === "wartet" || it.status === "laufend").length;
  useEffect(() => { setUploading(activeUploads > 0); }, [activeUploads]);
  const fileRef = useRef(null);
  const { t } = useLanguage();

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(res.data);
    } catch (e) { console.error(e); }
  };

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_usage: true };
      if (selectedCompany !== "all") params.company_id = selectedCompany;
      if (unusedOnly) params.unused_only = true;
      const res = await axios.get(`${API}/media`, { params, withCredentials: true });
      setMedia(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedCompany, unusedOnly]);

  useEffect(() => { loadCompanies(); }, []);
  useEffect(() => { loadMedia(); }, [loadMedia]);

  // Track blob URLs in a ref so the unmount cleanup sees the latest set
  const blobUrlsRef = useRef(new Set());
  useEffect(() => {
    Object.values(thumbnails).forEach((u) => u && blobUrlsRef.current.add(u));
    Object.values(variantThumbs).forEach((vs) =>
      Object.values(vs || {}).forEach((u) => u && blobUrlsRef.current.add(u))
    );
  }, [thumbnails, variantThumbs]);
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      blobUrlsRef.current.clear();
    };
  }, []);

  // Load thumbnails as blobs
  useEffect(() => {
    media.forEach((m) => {
      if ((m.content_type?.startsWith("image/") || m.content_type?.startsWith("video/")) && !thumbnails[m.id]) {
        axios.get(`${API}/media/thumb/${m.id}`, {
          withCredentials: true, responseType: "blob",
          params: { v: m.content_hash || m.storage_path || "" }
        }).then((res) => {
          const url = URL.createObjectURL(res.data);
          setThumbnails((prev) => ({ ...prev, [m.id]: url }));
        }).catch(() => {});
      }
    });
  }, [media]);

  const setItemStatus = (key, patch) => {
    setUploadQueue((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  };

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    let item;
    // eslint-disable-next-line no-cond-assign
    while ((item = queueRef.current.shift())) {
      setItemStatus(item.key, { status: "laufend" });
      const formData = new FormData();
      formData.append("file", item.file);
      formData.append("company_id", item.companyId);
      formData.append("category", item.file.type?.startsWith("video/") ? "videos" : "photos");
      if (item.note) formData.append("note", item.note);
      if (item.force) formData.append("force", "true");
      try {
        await axios.post(`${API}/media/upload`, formData, { withCredentials: true });
        setItemStatus(item.key, { status: "fertig" });
        loadMedia();
      } catch (e) {
        const detail = e.response?.data?.detail || "Upload fehlgeschlagen";
        if (e.response?.status === 409) {
          setItemStatus(item.key, { status: "duplikat", detail });
          toast.warning(detail, { duration: 8000 });
        } else {
          setItemStatus(item.key, { status: "fehler", detail });
          toast.error(`${item.name}: ${detail}`);
        }
      }
    }
    processingRef.current = false;
  };

  const handleUpload = (files) => {
    if (!files?.length) return;
    if (selectedCompany === "all") {
      toast.error("Bitte waehle zuerst eine Firma aus.");
      return;
    }
    const items = Array.from(files).map((file, i) => ({
      key: `${Date.now()}_${i}_${file.name}`,
      name: file.name,
      status: "wartet",
      detail: "",
      file,
      companyId: selectedCompany,
      note: uploadNote.trim(),
    }));
    setUploadQueue((prev) => [...prev.filter((it) => it.status === "laufend" || it.status === "wartet"), ...items]);
    queueRef.current.push(...items);
    toast.info(`${items.length} Datei(en) werden im Hintergrund hochgeladen — du kannst weiterarbeiten`);
    processQueue();
  };

  const forceUpload = (item) => {
    const retry = { ...item, key: `${item.key}_force`, status: "wartet", detail: "", force: true };
    setUploadQueue((prev) => prev.map((it) => (it.key === item.key ? retry : it)));
    queueRef.current.push(retry);
    processQueue();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const [bulkNote, setBulkNote] = useState(null);
  const [bulkNoteSaving, setBulkNoteSaving] = useState(false);
  const saveBulkNote = async () => {
    setBulkNoteSaving(true);
    try {
      const r = await axios.post(`${API}/media/bulk-note`, { media_ids: selectedIds, note: bulkNote.text }, { withCredentials: true });
      toast.success(r.data.message);
      setBulkNote(null);
      setSelectedIds([]);
      setSelectMode(false);
      loadMedia();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBulkNoteSaving(false); }
  };

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/media/bulk-delete`, { media_ids: selectedIds }, { withCredentials: true });
      toast.success(r.data.message);
      setSelectedIds([]);
      setSelectMode(false);
      setBulkConfirm(false);
      loadMedia();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    finally { setBulkDeleting(false); }
  };

  const [rotating, setRotating] = useState(null);
  const handleRotate = async (id) => {
    setRotating(id);
    try {
      const r = await axios.post(`${API}/media/${id}/rotate`, {}, { withCredentials: true });
      toast.success(r.data.message);
      setThumbnails((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setVariantThumbs((prev) => { const n = { ...prev }; delete n[id]; return n; });
      loadMedia();
    } catch (e) { toast.error(e.response?.data?.detail || "Drehen fehlgeschlagen"); }
    finally { setRotating(null); }
  };

  const handleDelete = async (id) => {
    try {
      const params = selectedCompany !== "all" ? { company_id: selectedCompany } : {};
      await axios.delete(`${API}/media/${id}`, { params, withCredentials: true });
      toast.success("Geloescht");
      loadMedia();
    } catch { toast.error("Fehler"); }
  };

  const saveNote = async () => {
    try {
      await axios.put(`${API}/media/${noteEdit.id}/note`, { note: noteEdit.text }, { withCredentials: true });
      toast.success("Notiz gespeichert");
      setNoteEdit(null);
      loadMedia();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const handleAnalyze = async (id) => {
    try {
      toast.info("Analyse gestartet...");
      await axios.post(`${API}/media/${id}/analyze`, {}, { withCredentials: true });
      toast.success("Analyse abgeschlossen");
      loadMedia();
    } catch { toast.error("Analyse fehlgeschlagen"); }
  };

  const openVariants = async (item) => {
    setVariantDialog(item);
    if (variantThumbs[item.id]) return; // already loaded
    const v = item.variants || {};
    const next = {};
    for (const key of Object.keys(v)) {
      try {
        const res = await axios.get(`${API}/media/file/${v[key].storage_path}`, {
          withCredentials: true, responseType: "blob"
        });
        next[key] = URL.createObjectURL(res.data);
      } catch (e) {
        console.error(`Variant ${key} load failed:`, e);
      }
    }
    setVariantThumbs((prev) => ({ ...prev, [item.id]: next }));
  };

  const downloadVariant = async (item, variantKey) => {
    const v = item.variants?.[variantKey];
    if (!v) return;
    try {
      const res = await axios.get(`${API}/media/file/${v.storage_path}`, {
        withCredentials: true, responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = (item.original_filename || "image").replace(/\.[^.]+$/, "");
      a.download = `${base}_${variantKey}_${v.width}x${v.height}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${variantKey} heruntergeladen`);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  const downloadAllAsZip = async (item) => {
    const variants = item.variants || {};
    const keys = Object.keys(variants);
    if (!keys.length) return;
    toast.info("ZIP wird erstellt…");
    try {
      const zip = new JSZip();
      const base = (item.original_filename || "image").replace(/\.[^.]+$/, "");
      const entries = await Promise.all(
        keys.map(async (key) => {
          const v = variants[key];
          const res = await axios.get(`${API}/media/file/${v.storage_path}`, {
            withCredentials: true, responseType: "blob"
          });
          return { key, blob: res.data, v };
        })
      );
      entries.forEach(({ key, blob, v }) => {
        zip.file(`${base}_${key}_${v.width}x${v.height}.jpg`, blob);
      });
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${base}_crops.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${keys.length} Varianten als ZIP`);
    } catch (e) {
      console.error(e);
      toast.error("ZIP-Download fehlgeschlagen");
    }
  };

  const handleFocalSaved = async ({ focal_point, focal_point_source, variants }) => {
    if (!variantDialog) return;
    const updatedItem = {
      ...variantDialog,
      focal_point,
      focal_point_source,
      variants,
      variant_paths: Object.values(variants || {}).map((v) => v.storage_path),
    };
    setVariantDialog(updatedItem);
    setMedia((prev) => prev.map((m) => (m.id === updatedItem.id ? { ...m, ...updatedItem } : m)));
    // Drop stale variant blob URLs and reload
    setVariantThumbs((prev) => {
      const copy = { ...prev };
      const old = copy[updatedItem.id] || {};
      Object.values(old).forEach((u) => u && URL.revokeObjectURL(u));
      delete copy[updatedItem.id];
      return copy;
    });
    const next = {};
    for (const key of Object.keys(variants || {})) {
      try {
        const res = await axios.get(`${API}/media/file/${variants[key].storage_path}`, {
          withCredentials: true, responseType: "blob"
        });
        next[key] = URL.createObjectURL(res.data);
      } catch (e) {
        console.error(`Variant ${key} reload failed:`, e);
      }
    }
    setVariantThumbs((prev) => ({ ...prev, [updatedItem.id]: next }));
  };

  const previewBackfill = async () => {
    setBackfillPreview({ loading: true });
    try {
      const body = selectedCompany !== "all" ? { dry_run: true, company_id: selectedCompany } : { dry_run: true };
      const res = await axios.post(`${API}/media/backfill-crops`, body, { withCredentials: true });
      setBackfillPreview({ ...res.data, loading: false });
    } catch (e) {
      console.error(e);
      toast.error("Backfill-Preview fehlgeschlagen");
      setBackfillPreview(null);
    }
  };

  const confirmBackfill = async () => {
    setBackfilling(true);
    try {
      const body = selectedCompany !== "all" ? { company_id: selectedCompany } : {};
      const res = await axios.post(`${API}/media/backfill-crops`, body, { withCredentials: true });
      toast.success(`${res.data.processed}/${res.data.total_candidates} Medien gecroppt` + (res.data.failed?.length ? ` · ${res.data.failed.length} Fehler` : ""));
      setBackfillPreview(null);
      loadMedia();
    } catch (e) {
      console.error(e);
      toast.error("Backfill fehlgeschlagen");
    } finally {
      setBackfilling(false);
    }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  return (
    <div data-testid="media-page" className="space-y-6 max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="media-title">
            {t("media.title")}{selectedCompany !== "all" ? ` — ${companies.find((c) => c.id === selectedCompany)?.name || ""}` : ""}
          </h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("media.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={previewBackfill}
            data-testid="backfill-crops-btn"
            className="h-9 text-xs"
            title="Fehlende Auto-Crop-Varianten fuer bestehende Medien generieren"
          >
            <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Legacy croppen
          </Button>
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-48" data-testid="media-company-filter">
              <SelectValue placeholder="Firma waehlen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={selectMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setSelectMode((v) => !v); setSelectedIds([]); }}
            className={`text-xs ${selectMode ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : ""}`}
            data-testid="select-mode-btn"
            title="Mehrere Medien auswaehlen — gemeinsam loeschen oder mit Notiz versehen"
          >
            {selectMode ? "✓ Auswählen" : "Auswählen"}
          </Button>
          {selectMode && (
            <>
              <Button variant="outline" size="sm" className="text-xs" data-testid="select-all-btn"
                onClick={() => setSelectedIds(selectedIds.length === media.length ? [] : media.map((m) => m.id))}>
                {selectedIds.length === media.length && media.length > 0 ? "Keine" : "Alle"}
              </Button>
              <Button size="sm" variant="outline" className="text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                disabled={selectedIds.length === 0}
                onClick={() => setBulkNote({ text: "" })} data-testid="bulk-note-btn"
                title="Dieselbe Notiz auf alle ausgewaehlten Medien setzen — die KI beruecksichtigt sie bei Posts & Reels">
                <StickyNote className="w-3.5 h-3.5 mr-1" /> Notiz ({selectedIds.length})
              </Button>
              <Button size="sm" className="text-xs bg-red-600 hover:bg-red-700 text-white"
                disabled={selectedIds.length === 0}
                onClick={() => setBulkConfirm(true)} data-testid="bulk-delete-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen ({selectedIds.length})
              </Button>
            </>
          )}
          <Button
            variant={unusedOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setUnusedOnly((v) => !v)}
            className={`text-xs ${unusedOnly ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white" : ""}`}
            data-testid="filter-unused-btn"
            title="Nur Medien zeigen, die noch in keinem Content verwendet wurden"
          >
            {unusedOnly ? "✓ Nur unverwendet" : "Nur unverwendet"}
          </Button>
        </div>
      </div>

      {/* Speicher-Übersicht + Aufräum-Assistent */}
      <StorageOverviewCard refreshKey={media.length} onCleaned={loadMedia} />

      {/* Upload Zone */}
      <div
        className="upload-zone"
        data-testid="upload-zone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
        onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("dragover"); handleUpload(e.dataTransfer.files); }}
      >
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
        />
        <Upload className={`w-10 h-10 mx-auto mb-3 ${uploading ? "animate-bounce text-[#2563EB]" : "text-[var(--studio-text-muted)]"}`} />
        <p className="text-sm font-medium">{uploading ? t("media.uploading") : t("media.upload_text")}</p>
        <p className="text-xs text-[var(--studio-text-muted)] mt-1">{t("media.upload_hint")}</p>
        {selectedCompany === "all" && (
          <p className="text-xs text-amber-600 mt-2">{t("media.select_company")}</p>
        )}
        <div className="mt-3 max-w-md mx-auto" onClick={(e) => e.stopPropagation()}>
          <Input
            value={uploadNote}
            onChange={(e) => setUploadNote(e.target.value)}
            maxLength={300}
            placeholder="Optionale Notiz zum Upload, z.B. 'Eröffnung neues Studio' — die KI berücksichtigt das"
            className="text-xs h-8"
            data-testid="upload-note-input"
          />
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="media-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-square bg-white rounded-lg animate-pulse" />
          ))}
        </div>
      ) : media.length === 0 ? (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardContent className="py-16 text-center">
            <Image className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
            <p className="text-[var(--studio-text-muted)]">{t("media.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="media-grid">
          {media.map((m) => (
            <div
              key={m.id}
              className={`media-item group ${selectMode && selectedIds.includes(m.id) ? "ring-4 ring-[#2563EB] ring-inset" : ""}`}
              data-testid={`media-item-${m.id}`}
              onClick={selectMode ? () => toggleSelect(m.id) : undefined}
            >
              {selectMode && (
                <div className={`absolute top-2 right-2 z-20 w-6 h-6 rounded-full border-2 flex items-center justify-center shadow ${selectedIds.includes(m.id) ? "bg-[#2563EB] border-[#2563EB]" : "bg-white/90 border-gray-300"}`}
                  data-testid={`select-check-${m.id}`}>
                  {selectedIds.includes(m.id) && <Check className="w-4 h-4 text-white" />}
                </div>
              )}
              {(m.content_type?.startsWith("image/") || m.content_type?.startsWith("video/")) && thumbnails[m.id] ? (
                <div className="relative w-full">
                  <img src={thumbnails[m.id]} alt={m.original_filename} className="w-full h-auto block" />
                  {m.content_type?.startsWith("video/") && !selectMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPreviewVideo({ url: `${API}/media/file/${m.storage_path}`, name: m.original_filename }); }}
                      className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center z-10"
                      title="Video abspielen"
                      data-testid={`play-video-${m.id}`}
                    >
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    </button>
                  )}
                </div>
              ) : m.content_type?.startsWith("video/") ? (
                <div className="w-full aspect-square bg-gray-900 flex items-center justify-center">
                  <Film className="w-8 h-8 text-white/60" />
                </div>
              ) : (
                <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                  <FileImage className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="overlay">
                <div className="w-full">
                  <p className="text-white text-xs truncate font-medium">{m.original_filename}</p>
                  <p className="text-white/60 text-[10px]">{companyName(m.company_id)}</p>
                  {m.note && (
                    <p className="text-amber-200 text-[10px] truncate flex items-center gap-1 mt-0.5" data-testid={`media-note-${m.id}`} title={m.note}>
                      <StickyNote className="w-2.5 h-2.5 shrink-0" /> {m.note}
                    </p>
                  )}
                  {m.ai_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {m.ai_tags.slice(0, 3).map((t, i) => (
                        <span key={i} className="text-[9px] bg-white/20 rounded px-1">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Usage badge — top-left */}
              {typeof m.used_in_count === "number" && (
                <div className="absolute top-2 left-2 z-10" data-testid={`usage-badge-${m.id}`}>
                  {m.used_in_count > 0 ? (
                    <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-medium shadow">
                      {m.used_in_count}× verwendet
                    </span>
                  ) : (
                    <span className="text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium shadow">
                      Neu
                    </span>
                  )}
                </div>
              )}
              {/* Action buttons on hover */}
              <div className={`absolute top-2 right-2 flex gap-1 transition-opacity ${selectMode ? "hidden" : "opacity-0 group-hover:opacity-100"}`}>
                <button
                  onClick={(e) => { e.stopPropagation(); setNoteEdit({ id: m.id, name: m.original_filename, text: m.note || "" }); }}
                  className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-amber-50"
                  data-testid={`note-media-${m.id}`}
                  title={m.note ? `Notiz bearbeiten: ${m.note}` : "Notiz hinzufuegen (KI-Kontext)"}
                >
                  <StickyNote className={`w-3 h-3 ${m.note ? "text-amber-500" : "text-slate-400"}`} />
                </button>
                {m.variants && Object.keys(m.variants).length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openVariants(m); }}
                    className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-[#FFE5E0]"
                    data-testid={`variants-media-${m.id}`}
                    title="Auto-Crop Varianten (IG 1:1, Story 9:16)"
                  >
                    <Crop className="w-3 h-3 text-[#2563EB]" />
                  </button>
                )}
                {!m.content_type?.startsWith("video/") && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRotate(m.id); }}
                    className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-blue-50"
                    data-testid={`rotate-media-${m.id}`}
                    title="Bild um 90° drehen"
                    disabled={rotating === m.id}
                  >
                    <RotateCw className={`w-3 h-3 text-[#2563EB] ${rotating === m.id ? "animate-spin" : ""}`} />
                  </button>
                )}
                {!m.content_type?.startsWith("video/") && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAnalyze(m.id); }}
                    className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white"
                    data-testid={`analyze-media-${m.id}`}
                    title="KI-Analyse"
                  >
                    <RefreshCw className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const a = document.createElement("a");
                    a.href = `${API}/media/file/${m.storage_path}?download=1`;
                    a.download = m.original_filename || "";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  }}
                  className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-emerald-50"
                  data-testid={`download-media-${m.id}`}
                  title="Datei herunterladen"
                >
                  <Download className="w-3 h-3 text-emerald-600" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}
                  className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-red-50"
                  data-testid={`delete-media-${m.id}`}
                  title="Loeschen"
                >
                  <Trash2 className="w-3 h-3 text-red-500" />
                </button>
              </div>
              {/* Variant badge (bottom-left) */}
              {m.variants && Object.keys(m.variants).length > 0 && (
                <div className="absolute bottom-2 left-2 flex gap-1">
                  <span
                    className="text-[9px] bg-[#0F0F0F]/80 text-white px-1.5 py-0.5 rounded font-mono font-semibold"
                    data-testid={`variant-badge-${m.id}`}
                    title="Automatisch zugeschnittene Varianten verfügbar"
                  >
                    {Object.keys(m.variants).length}× Crop
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* AI Analysis Details */}
      {media.filter((m) => m.ai_analysis).length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{t("media.ai_analyses")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {media.filter((m) => m.ai_analysis).slice(0, 5).map((m) => (
              <div key={m.id} className="p-3 rounded-lg bg-[var(--studio-bg)] text-sm">
                <p className="font-medium">{m.original_filename}</p>
                <p className="text-[var(--studio-text-muted)] text-xs mt-1">
                  {m.ai_analysis?.description || m.ai_analysis?.raw_response?.substring(0, 200)}
                </p>
                {m.ai_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {m.ai_tags.map((t, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {/* Variants Dialog */}
      <Dialog open={!!noteEdit} onOpenChange={(o) => !o && setNoteEdit(null)}>
        <DialogContent className="sm:max-w-md" data-testid="media-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" /> Notiz zum Medium
            </DialogTitle>
          </DialogHeader>
          {noteEdit && (
            <div className="space-y-3">
              <p className="text-xs text-[var(--studio-text-muted)]">{noteEdit.name}</p>
              <Textarea
                rows={3}
                maxLength={300}
                value={noteEdit.text}
                onChange={(e) => setNoteEdit((n) => ({ ...n, text: e.target.value }))}
                placeholder="z.B. 'Eröffnung unseres neuen Studios' — die KI berücksichtigt diese Notiz beim Erstellen von Posts und Reels"
                data-testid="media-note-textarea"
              />
              <div className="flex gap-2">
                <Button onClick={saveNote} className="flex-1" data-testid="media-note-save">Speichern</Button>
                {noteEdit.text && (
                  <Button variant="outline" onClick={() => setNoteEdit((n) => ({ ...n, text: "" }))} data-testid="media-note-clear">Leeren</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Bulk Note Dialog */}
      <Dialog open={!!bulkNote} onOpenChange={(o) => !o && setBulkNote(null)}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-amber-500" /> Notiz für {selectedIds.length} Medien
            </DialogTitle>
          </DialogHeader>
          {bulkNote && (
            <div className="space-y-3">
              <p className="text-xs text-[var(--studio-text-muted)]">
                Die Notiz wird auf alle {selectedIds.length} ausgewählten Medien gesetzt. Die KI berücksichtigt
                sie beim Erstellen von Posts und Reels — die Bildanalyse läuft wie gewohnt zusätzlich.
              </p>
              <Textarea
                rows={3}
                maxLength={300}
                value={bulkNote.text}
                onChange={(e) => setBulkNote((n) => ({ ...n, text: e.target.value }))}
                placeholder="z.B. 'Eröffnung neues Studio am 1. Juli' — gilt für alle ausgewählten Bilder"
                data-testid="bulk-note-textarea"
              />
              <Button onClick={saveBulkNote} disabled={bulkNoteSaving || !bulkNote.text.trim()} className="w-full" data-testid="bulk-note-save">
                {bulkNoteSaving ? "Speichert…" : `Auf ${selectedIds.length} Medien anwenden`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!previewVideo} onOpenChange={(o) => !o && setPreviewVideo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="library-video-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Play className="w-5 h-5 text-[#2563EB]" /> Video-Vorschau
            </DialogTitle>
          </DialogHeader>
          {previewVideo && (
            <video
              src={previewVideo.url}
              controls
              autoPlay
              playsInline
              className="w-full max-h-[60vh] rounded-lg bg-black"
              data-testid="library-video-player"
            />
          )}
          <p className="text-[11px] text-[var(--studio-text-muted)] truncate">{previewVideo?.name}</p>
        </DialogContent>
      </Dialog>

      <Dialog open={!!variantDialog} onOpenChange={(o) => !o && setVariantDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="variants-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="font-heading flex items-center gap-2">
                <Crop className="w-5 h-5 text-[#2563EB]" />
                Auto-Crop Varianten – {variantDialog?.original_filename}
              </DialogTitle>
              {variantDialog && Object.keys(variantDialog.variants || {}).length > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadAllAsZip(variantDialog)}
                  data-testid="download-all-zip-btn"
                  className="h-8 text-xs"
                >
                  <Archive className="w-3 h-3 mr-1" /> Alle als ZIP
                </Button>
              )}
            </div>
          </DialogHeader>
          {variantDialog && (
            <div className="space-y-6 mt-2">
              <FocalPointEditor
                media={variantDialog}
                thumbUrl={thumbnails[variantDialog.id]}
                onSaved={handleFocalSaved}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(variantDialog.variants || {}).map(([key, v]) => {
                  const label = key === "instagram_post" ? "Instagram Post (1:1)"
                    : key === "instagram_feed_4_5" ? "Feed Post Hochformat (4:5)"
                    : key === "instagram_story" ? "Instagram Story / Reel (9:16)"
                    : key;
                  const thumb = variantThumbs[variantDialog.id]?.[key];
                  return (
                    <div key={key} className="space-y-3" data-testid={`variant-card-${key}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold">{label}</p>
                          <p className="text-xs text-[var(--studio-text-muted)] font-mono">
                            {v.width}×{v.height} · {Math.round(v.size / 1024)} KB
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => downloadVariant(variantDialog, key)}
                          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-8 text-xs"
                          data-testid={`download-variant-${key}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      </div>
                      <div
                        className="rounded-lg bg-[var(--studio-bg)] overflow-hidden flex items-center justify-center border border-[var(--studio-border)]"
                        style={{ aspectRatio: `${v.width}/${v.height}`, maxHeight: 380 }}
                      >
                        {thumb ? (
                          <img src={thumb} alt={label} className="w-full h-full object-contain" />
                        ) : (
                          <div className="animate-pulse text-xs text-[var(--studio-text-muted)]">Lade…</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Backfill Confirmation Dialog */}
      <AlertDialog open={!!backfillPreview} onOpenChange={(o) => !o && !backfilling && setBackfillPreview(null)}>
        <AlertDialogContent data-testid="backfill-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#2563EB]" />
              Legacy-Medien croppen
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {backfillPreview?.loading ? (
                  <p>Analysiere bestehende Medien…</p>
                ) : backfillPreview?.total_candidates === 0 ? (
                  <p>Alle Bilder haben bereits Auto-Crop-Varianten. Nichts zu tun.</p>
                ) : (
                  <>
                    <p>
                      <span className="font-semibold text-[#2563EB]" data-testid="backfill-count">
                        {backfillPreview?.total_candidates}
                      </span>{" "}
                      Medien ohne Varianten gefunden
                      {selectedCompany !== "all" ? " (nur gewaehlte Firma)" : ""}.
                    </p>
                    {backfillPreview?.sample?.length > 0 && (
                      <div className="rounded-lg bg-[var(--studio-bg)] p-2 max-h-32 overflow-y-auto">
                        <p className="text-xs text-[var(--studio-text-muted)] mb-1">Beispiele:</p>
                        {backfillPreview.sample.map((f, i) => (
                          <p key={i} className="text-xs font-mono truncate">{f}</p>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-[var(--studio-text-muted)]">
                      Erzeugt je Medium 1:1 + 9:16 Varianten. Kein Original wird ueberschrieben.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backfill-cancel-btn" disabled={backfilling}>Abbrechen</AlertDialogCancel>
            {backfillPreview?.total_candidates > 0 && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmBackfill(); }}
                disabled={backfilling}
                data-testid="backfill-confirm-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                {backfilling ? "Verarbeite…" : "Jetzt croppen"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mehrfach-Löschen Bestätigung */}
      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{selectedIds.length} Datei(en) löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Die ausgewählten Bilder/Videos werden aus der Mediathek entfernt. Bereits veröffentlichte Posts bleiben unverändert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel" disabled={bulkDeleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); bulkDelete(); }} disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-confirm">
              {bulkDeleting ? "Lösche…" : "Endgültig löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hintergrund-Upload-Panel */}
      {uploadQueue.length > 0 && (
        <div className="fixed bottom-4 right-4 w-80 bg-white border border-[var(--studio-border)] rounded-xl shadow-xl z-50 overflow-hidden" data-testid="upload-queue-panel">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
            <span className="text-xs font-semibold">
              {activeUploads > 0 ? `⬆️ Lädt im Hintergrund (${activeUploads} offen)…` : "✅ Uploads abgeschlossen"}
            </span>
            <button className="text-gray-400 hover:text-gray-600 text-xs" data-testid="upload-queue-close"
              onClick={() => setUploadQueue((prev) => prev.filter((it) => it.status === "wartet" || it.status === "laufend"))}>
              Schliessen
            </button>
          </div>
          <div className="max-h-56 overflow-auto divide-y">
            {uploadQueue.map((it) => (
              <div key={it.key} className="px-3 py-1.5 text-xs" data-testid={`upload-item-${it.status}`}>
                <div className="flex items-center gap-2">
                  {it.status === "laufend" && <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />}
                  {it.status === "wartet" && <span className="text-gray-400 shrink-0">⏳</span>}
                  {it.status === "fertig" && <span className="text-emerald-600 shrink-0">✓</span>}
                  {it.status === "duplikat" && <span className="text-amber-600 shrink-0">⚠️</span>}
                  {it.status === "fehler" && <span className="text-red-600 shrink-0">✗</span>}
                  <span className="truncate flex-1">{it.name}</span>
                </div>
                {it.status === "duplikat" && (
                  <div className="mt-0.5 pl-5">
                    <p className="text-amber-700">{it.detail}</p>
                    <button className="text-blue-600 underline mt-0.5" onClick={() => forceUpload(it)} data-testid="upload-force-btn">
                      Trotzdem hochladen
                    </button>
                  </div>
                )}
                {it.status === "fehler" && <p className="mt-0.5 pl-5 text-red-600">{it.detail}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
